import React from 'react';
import BaseModal from './BaseModal';
import Input from '../common/Input';
import Button from '../common/Button';
import { useContract } from '../../hooks/useContract';

const ListForSaleModal = ({
  isOpen,
  onClose,
  priceInput,
  setPriceInput,
  onSubmit,
  minPrice = '0',
  nft,
  account
}) => {
  const { getExpirationInfo } = useContract(account);
  const expirationInfo = nft ? getExpirationInfo(nft.expires_at) : { canList: true, isExpired: false, isLifetime: false };
  const isPriceValid = priceInput && Number(priceInput) > 0 && Number(priceInput) >= Number(minPrice);
  const canList = !expirationInfo.isExpired && expirationInfo.canList;

  const handleSubmit = () => {
    if (!isPriceValid) return;
    onSubmit();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="List License for Sale"
      subtitle="Set your asking price"
      icon="💰"
      footer={
        <div style={{
          display: 'flex',
          gap: window.innerWidth < 640 ? '12px' : '16px', // Responsive gap
          flexDirection: window.innerWidth < 640 ? 'column' : 'row' // Stack buttons on mobile
        }}>
          <Button
            variant="secondary"
            onClick={onClose}
            style={{
              width: window.innerWidth < 640 ? '100%' : 'auto', // Full width on mobile
              height: window.innerWidth < 640 ? '52px' : '48px', // Taller on mobile
              fontSize: window.innerWidth < 640 ? '16px' : '14px' // Larger font on mobile
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSubmit}
            disabled={!isPriceValid || !canList}
            style={{
              width: window.innerWidth < 640 ? '100%' : 'auto', // Full width on mobile
              height: window.innerWidth < 640 ? '52px' : '48px', // Taller on mobile
              fontSize: window.innerWidth < 640 ? '16px' : '14px' // Larger font on mobile
            }}
          >
            List for Sale
          </Button>
        </div>
      }
    >
      <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: window.innerWidth < 640 ? '16px' : '24px' // Responsive spacing
        }}>
        {/* Price Input Section */}
        <div className="bg-dark-surface/50 backdrop-blur-sm rounded-xl border border-dark-border" style={{
          padding: window.innerWidth < 640 ? '16px' : '24px' // Responsive padding
        }}>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: window.innerWidth < 640 ? '12px' : '16px' // Responsive spacing
        }}>
            <div>
              <label className="block text-text-primary text-sm font-medium mb-2 flex justify-between">
                <span>Price</span>
                {Number(minPrice) > 0 && (
                  <span className="text-etc-primary text-xs font-normal">
                    Min Price: {minPrice} ETCPOWv2
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={priceInput}
                  onChange={(e) => setPriceInput(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  style={{
                    width: '100%',
                    fontSize: window.innerWidth < 640 ? '16px' : '14px', // Larger font on mobile
                    padding: window.innerWidth < 640 ? '14px 16px' : '12px 16px', // Larger padding on mobile
                    height: window.innerWidth < 640 ? '52px' : '48px', // Taller on mobile for easier touch
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    WebkitAppearance: 'none', // Better mobile appearance
                    MozAppearance: 'textfield'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(59, 130, 246, 0.5)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <span className="text-text-muted text-sm font-medium">ETCPOWv2</span>
                </div>
              </div>
            </div>

            {/* Price Validation Error */}
            {priceInput && Number(priceInput) > 0 && Number(priceInput) < Number(minPrice) && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-xs font-medium flex items-center gap-2">
                  <span>⚠️</span> Price must be at least {minPrice} ETCPOWv2
                </p>
              </div>
            )}

            {/* Expiration Warnings */}
            {expirationInfo.isExpired && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-xs font-medium flex items-center gap-2">
                  <span>⏰</span> This license has expired and cannot be listed
                </p>
              </div>
            )}

            {!expirationInfo.isExpired && !expirationInfo.canList && !expirationInfo.isLifetime && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-yellow-400 text-xs font-medium flex items-center gap-2">
                  <span>⚠️</span> License expires in {expirationInfo.remainingTime}. Must have at least 7 days remaining to list.
                </p>
              </div>
            )}

            {/* Info Tip */}
            <div className="flex items-start gap-3 p-3 bg-etc-primary/10 border border-etc-primary/20 rounded-lg">
              <span className="text-etc-primary text-lg mt-0.5">💡</span>
              <div>
                <p className="text-text-primary text-sm font-medium mb-1">Listing Info</p>
                <p className="text-text-secondary text-xs leading-relaxed">
                  Enter the exact price in ETCPOWv2. Your license will be listed on the marketplace immediately after confirmation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {priceInput && Number(priceInput) > 0 && (
          <div className="bg-gradient-to-r from-etc-primary/10 to-blue-500/10 rounded-xl border border-etc-primary/20" style={{
          padding: window.innerWidth < 640 ? '12px' : '16px' // Responsive padding
        }}>
            <div className="flex items-center justify-between">
              <span className="text-text-muted text-sm">Listing Price:</span>
              <span className="text-etc-primary text-lg font-semibold">
                {Number(priceInput).toFixed(2)} ETCPOWv2
              </span>
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default ListForSaleModal;
